/*
 * Licensed Materials - Property of IBM
 *
 * PID 5725-H26
 *
 * Copyright IBM Corporation 2018,2019. All Rights Reserved.
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
/**
 * Combine all reducers in this file and export the combined reducers.
 * If we were to do this in store.js, reducers wouldn't be hot re-loadable.
 */
import ReduxUtils from '../../utils/ReduxUtils';
import { intlConfigReducer } from '../IntlConfig/reducers';
import AuthenticationReducers from '../Authentication/reducers';
import ProfileReducers from '../Profile/reducers';
import SystemConfigurationReducers from '../SystemConfiguration/reducers';
import UserPreferencesReducers from '../UserPreferences/reducers';
import SSOVerifierReducers from '../SSOVerifier/reducers';
import ToasterReducers from '../Toaster/reducers';
import ConnectivityReducer from '../Connectivity/reducers';
import UserReducers from '../User/reducers';
import SignUpReducers from '../Signup/reducers';
import DocumentTitleReducers from '../DocumentTitle/reducers';

/**
 * Creates the main reducer which combines all the reducers from the modules
 * and any provided reducers supplied from the wrapping application.
 *
 * Provided reducers are ignored where there is a clash in naming.
 *
 * @namespace ReduxReducers
 */
export default {
  /**
   * Holds the application configuration state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UAIntlConfig: intlConfigReducer,
  /**
   * Holds the authentication state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UAAuthentication: AuthenticationReducers.authenticationReducer,
  /**
   * Holds the user profile state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UAProfile: ProfileReducers.reducers,
  /**
   * Holds the system configuration state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UASystemConfiguration: SystemConfigurationReducers.reducers,
  /**
   * Holds the user preferences state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UAUserPreferences: UserPreferencesReducers.userPreferencesReducer,
  /**
   * Holds the SSOVerifier state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UASSOVerifier: SSOVerifierReducers.ssoVerifierReducer,
  /**
   * Holds the toast state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UAToaster: ToasterReducers.toastReducer,
  /**
   * Sets the online state within the store.
   * @memberof ReduxReducers
   * @property
   */
  CoreConnectivity: ConnectivityReducer.connectivityReducer,
  /**
   * Holds the app spinner state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UAAppSpinner: ReduxUtils.generateGlobalFetchingReducer,
  /**
   * Holds the global error state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UAErrors: ReduxUtils.generateGlobalErrorReducer,
  /**
   * Holds the application configuration state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UAUser: UserReducers.reducers,
  /**
   * Holds the application configuration state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UASignup: SignUpReducers.reducers,
  /**
   * Holds the document title state within the store.
   * @memberof ReduxReducers
   * @property
   */
  UADocumentTitle: DocumentTitleReducers.documentTitleReducer
};